import { useNavigate } from 'react-router-dom';
import { RiCheckboxCircleLine } from '@remixicon/react';
import Button from '@components/ui/Button';

/**
 * Shown when a user clicks a Supabase verification link on a
 * different device / browser from where they signed up. The email
 * is confirmed server-side but the PKCE code_verifier lives only
 * on the original device, so the client-side session exchange
 * fails. The user needs to sign in once here to complete the flow.
 *
 * Used by both `/email-confirmation` (normal signup) and
 * `/register/team` (invite acceptance), so the invitee and the
 * new signup both see the same recovery UX.
 *
 * Props:
 *   title    — heading. Defaults to "Email confirmed".
 *   subtitle — optional secondary line. Callers pass flow-specific
 *              context (e.g. "…to finish accepting your invitation.").
 *   ctaLabel — button text. Defaults to "Go to Sign In".
 *   ctaTo    — navigate target. Defaults to '/'.
 */
export default function CrossDeviceRecovery({
  title = 'Email confirmed',
  subtitle,
  ctaLabel = 'Go to Sign In',
  ctaTo = '/',
}) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="max-w-md w-full flex flex-col flex-1">
        <div className="text-center lg:text-left mb-6">
          <h2 className="text-3xl font-bold text-primary-500">{title}</h2>
          {subtitle && <p className="text-gray-600 mt-2">{subtitle}</p>}
        </div>

        <div className="flex flex-col items-center justify-center flex-1 py-6">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-6">
            <RiCheckboxCircleLine className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-sm text-gray-700 text-center mb-6">
            Your email is verified — one quick sign-in and you're in.
          </p>
          <Button
            type="button"
            className="w-full !font-normal bg-primary-500 hover:bg-primary-600 rounded-full"
            onClick={() => navigate(ctaTo)}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
